import React, { useState, useEffect } from "react";
import ApiService from "../services/ApiService";
import "./CarTypeManagement.css";

const CarTypeManagement = () => {
  const [carTypes, setCarTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCarType, setEditingCarType] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    active: true,
  });

  useEffect(() => {
    fetchCarTypes();
  }, []);

  const fetchCarTypes = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getCarTypes(true); // Include inactive
      setCarTypes(response.data || []);
      setError("");
    } catch (error) {
      console.error("Error fetching car types:", error);
      setError("Failed to fetch car types");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }

    try {
      setLoading(true);

      if (editingCarType) {
        await ApiService.updateCarType(editingCarType.id, formData);
      } else {
        await ApiService.createCarType(formData);
      }

      await fetchCarTypes();
      resetForm();
      setError("");
    } catch (error) {
      console.error("Error saving car type:", error);
      setError(error.message || "Failed to save car type");
    } finally {
      setLoading(false);
    }
  };
  const handleEdit = (carType) => {
    setEditingCarType(carType);
    setFormData({
      name: carType.name,
      active: carType.active,
    });
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this car type?")) {
      return;
    }

    try {
      setLoading(true);
      await ApiService.deleteCarType(id);
      await fetchCarTypes();
      setError("");
    } catch (error) {
      console.error("Error deleting car type:", error);
      setError(error.message || "Failed to delete car type");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      setLoading(true);
      await ApiService.restoreCarType(id);
      await fetchCarTypes();
      setError("");
    } catch (error) {
      console.error("Error restoring car type:", error);
      setError(error.message || "Failed to restore car type");
    } finally {
      setLoading(false);
    }
  };
  const resetForm = () => {
    setFormData({
      name: "",
      active: true,
    });
    setEditingCarType(null);
    setShowAddModal(false);
    setShowEditModal(false);
  };
  // Simple list view instead of grouping by category

  if (loading && carTypes.length === 0) {
    return (
      <div className="car-type-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading car types...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="car-type-management dark-theme">
      <div className="car-type-header">
        <h2>Car Type Management</h2>
        <button
          className="add-car-type-btn"
          onClick={() => setShowAddModal(true)}
          disabled={loading}
        >
          Add New Car Type
        </button>
      </div>
      {error && <div className="error-message">{error}</div>}{" "}
      <div className="car-types-content">
        {carTypes.length === 0 ? (
          <div className="no-car-types">
            <p>No car types found. Add your first car type to get started.</p>
          </div>
        ) : (
          <div className="car-types-grid">
            {carTypes.map((carType) => (
              <div
                key={carType.id}
                className={`car-type-card ${!carType.active ? "inactive" : ""}`}
              >
                <div className="car-type-info">
                  <h4>{carType.name}</h4>
                  <span
                    className={`status-badge ${
                      carType.active ? "active" : "inactive"
                    }`}
                  >
                    {carType.active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="car-type-actions">
                  <button
                    className="edit-btn"
                    onClick={() => handleEdit(carType)}
                    disabled={loading}
                  >
                    Edit
                  </button>
                  {carType.active ? (
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(carType.id)}
                      disabled={loading}
                    >
                      Delete
                    </button>
                  ) : (
                    <button
                      className="restore-btn"
                      onClick={() => handleRestore(carType.id)}
                      disabled={loading}
                    >
                      Restore
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Add Car Type Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Car Type</h3>
              <button className="close-btn" onClick={resetForm}>
                ×
              </button>
            </div>{" "}
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., BMW 3 Series, Toyota Camry"
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={resetForm}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="save-btn">
                  {loading ? "Adding..." : "Add Car Type"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Edit Car Type Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Car Type</h3>
              <button className="close-btn" onClick={resetForm}>
                ×
              </button>
            </div>{" "}
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="edit-name">Name *</label>
                <input
                  type="text"
                  id="edit-name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., BMW 3 Series, Toyota Camry"
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={resetForm}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="save-btn">
                  {loading ? "Updating..." : "Update Car Type"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarTypeManagement;
